// Pure helpers for the "reveal a hidden terminal as a temporary lane"
// feature. Temporary ports are interleaved into the lane ordering
// after the visible neighbour they were revealed from (the anchor).
// Orphaned temps — anchor no longer in the merged tree — drop out.

// One temp lane's full identity. The store holds a single
// Record<port, TemporaryLane> — atomic by construction; the five
// parallel maps this replaced had to be kept in sync by convention
// (the "must click twice" bug came from exactly that split).
export interface TemporaryLane {
  anchor: string;         // perm/temp port this lane sits after
  sourceVessel: string;   // vesselCode whose journey revealed it
  sourceVoyageNo: string;
  orderHint: number;      // originating hidden stop's timeMs
}

// Derived views for the positional pure helpers below.
export function laneAnchors(lanes: Readonly<Record<string, TemporaryLane>>): Record<string, string> {
  return Object.fromEntries(Object.entries(lanes).map(([k, v]) => [k, v.anchor]));
}

export function laneOrderHints(lanes: Readonly<Record<string, TemporaryLane>>): Record<string, number> {
  return Object.fromEntries(Object.entries(lanes).map(([k, v]) => [k, v.orderHint]));
}

export function computeOrderedQueryPorts(
  selected: readonly string[],
  temps: readonly string[],
  anchors: Readonly<Record<string, string>>,
  // Optional per-temp sort hint (timeMs of the originating hidden
  // stop). Temps sharing an anchor are emitted in ascending hint
  // order so multi-hidden-stop reveals land in the order the icons
  // appear along the journey bezier (earliest stop first).
  orderHints?: Readonly<Record<string, number>>,
): string[] {
  const tempsByAnchor = new Map<string, string[]>();
  for (const t of temps) {
    const a = anchors[t];
    if (!a) continue;
    if (!tempsByAnchor.has(a)) tempsByAnchor.set(a, []);
    tempsByAnchor.get(a)!.push(t);
  }
  if (orderHints) {
    for (const children of tempsByAnchor.values()) {
      children.sort((p, q) => (orderHints[p] ?? 0) - (orderHints[q] ?? 0));
    }
  }
  const out: string[] = [];
  const visited = new Set<string>();
  function emit(p: string) {
    if (visited.has(p)) return;
    visited.add(p);
    out.push(p);
    const children = tempsByAnchor.get(p);
    if (children) for (const c of children) emit(c);
  }
  for (const p of selected) emit(p);
  return out;
}

// Resolve a candidate anchor port to one that's actually in
// selectedPorts. When the user clicks a second hidden stop while an
// existing temp lane is still active, the journey segment they
// clicked may sit between the temp lane and another cluster — so the
// naïve anchor is the TEMP port. addTemporaryPort wholesale-replaces
// the existing temp, which leaves the new temp anchored to a now-
// removed port; computeOrderedQueryPorts then drops it as an orphan
// and the new lane never appears. Walk temporaryAnchors back to the
// underlying selected port; defensive fallback to the first selected
// port if the chain doesn't terminate there.
export function resolveSelectedAnchor(
  candidate: string,
  selected: readonly string[],
  temps: readonly string[],
  anchors: Readonly<Record<string, string>>,
): string {
  const tempsSet = new Set(temps);
  const seen = new Set<string>();
  let cur = candidate;
  while (tempsSet.has(cur) && !seen.has(cur)) {
    seen.add(cur);
    const parent = anchors[cur];
    if (!parent || parent === cur) break;
    cur = parent;
  }
  if (selected.includes(cur)) return cur;
  return selected[0] ?? candidate;
}

// Drop temps whose anchor isn't reachable from selectedPorts via the
// (recursive) anchor chain — keeps temps consistent when the user
// removes a permanent lane that a temp was hanging off of.
export function pruneOrphanedTemps(
  selected: readonly string[],
  temps: readonly string[],
  anchors: Readonly<Record<string, string>>,
): { temps: string[]; anchors: Record<string, string> } {
  const ordered = computeOrderedQueryPorts(selected, temps, anchors);
  const live = new Set(ordered);
  const nextTemps = temps.filter((t) => live.has(t));
  const nextAnchors: Record<string, string> = {};
  for (const t of nextTemps) {
    const a = anchors[t];
    if (a) nextAnchors[t] = a;
  }
  return { temps: nextTemps, anchors: nextAnchors };
}
